import React, { useEffect, useState } from "react";
import axios from "axios";
import { Leads } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Mold Remediation",
  "Water Damage Restoration",
  "Flood Damage",
  "Equipment Inquiry",
  "Other",
];

const INQUIRY_TYPES = [
  "Water damage",
  "Mold concern",
  "Equipment information",
  "Disaster-response package",
  "Generator-supported configuration",
  "Professional authorization",
  "Insurance program",
  "Commercial project",
  "Other",
];

const DAMAGE_TIMING = [
  "Within 24 hours",
  "24–48 hours",
  "48–72 hours",
  "More than 72 hours",
  "Not sure",
  "No water damage / equipment inquiry only",
];

const POWER_AVAILABLE = [
  "Yes",
  "No",
  "Limited or unstable power",
  "Not sure",
  "Not applicable",
];

const INDUSTRIES = [
  "Homeowner",
  "Property manager",
  "Restoration company",
  "Remediation company",
  "General contractor",
  "Insurance / adjuster",
  "Commercial / hotel / hospitality",
  "Municipal / disaster response",
  "Other",
];

const DEFAULT_VIS = {
  show_name: true, show_email: true, show_phone: true, show_company: true,
  show_address: true, show_license_number: false, show_industry: false,
  show_inquiry_type: true, show_service: true, show_damage_timing: true,
  show_power_available: true, show_message: true,
};

const QuoteForm = ({ defaultService = "", packageId, packageName, compact = false }) => {
  const [vis, setVis] = useState(DEFAULT_VIS);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", address: "",
    license_number: "", industry: "",
    inquiry_type: "",
    service: defaultService || (packageName ? "Equipment Inquiry" : ""),
    damage_timing: "", power_available: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/contact-form-settings`);
        setVis((v) => ({ ...v, ...data }));
      } catch { /* keep defaults */ }
    })();
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (vis.show_name && !form.name) { toast.error("Please enter your name."); return; }
    if (vis.show_email && !form.email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      // Only submit visible fields (avoid leaking hidden defaults into leads)
      const payload = { package_id: packageId || null, package_name: packageName || null };
      const map = {
        show_name: "name", show_email: "email", show_phone: "phone",
        show_company: "company", show_address: "address",
        show_license_number: "license_number", show_industry: "industry",
        show_inquiry_type: "inquiry_type", show_service: "service",
        show_damage_timing: "damage_timing", show_power_available: "power_available",
        show_message: "message",
      };
      Object.entries(map).forEach(([flag, field]) => {
        if (vis[flag]) payload[field] = form[field] || "";
      });
      // Name & email are required backend-side even if turned off — send blanks so validation passes
      if (!payload.name) payload.name = form.name || "—";
      if (!payload.email) payload.email = form.email || "unknown@example.com";

      await Leads.create(payload);
      toast.success("Thanks! We'll be in touch within 1 business day.");
      setForm((f) => ({
        ...f, name: "", email: "", phone: "", company: "", address: "",
        license_number: "", industry: "", inquiry_type: "",
        damage_timing: "", power_available: "", message: "",
      }));
    } catch {
      toast.error("Couldn't send your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-slate-950/60 border-white/10 text-white placeholder:text-slate-500";
  const triggerCls = "bg-slate-950/60 border-white/10 text-white";
  const contentCls = "bg-slate-900 border-white/10 text-white";

  return (
    <form
      onSubmit={submit}
      className={`glass rounded-2xl p-6 sm:p-8 ${compact ? "" : "max-w-2xl"}`}
      data-testid="quote-form"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        {vis.show_name && (
          <Input placeholder="Full name" value={form.name} onChange={update("name")} className={inputCls} data-testid="quote-name" required />
        )}
        {vis.show_email && (
          <Input type="email" placeholder="Email address" value={form.email} onChange={update("email")} className={inputCls} data-testid="quote-email" required />
        )}
        {vis.show_phone && (
          <Input placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} className={inputCls} data-testid="quote-phone" />
        )}
        {vis.show_company && (
          <Input placeholder="Company (optional)" value={form.company} onChange={update("company")} className={inputCls} data-testid="quote-company" />
        )}
        {vis.show_address && (
          <Input placeholder="Property address / project location" value={form.address} onChange={update("address")} className={`${inputCls} sm:col-span-2`} data-testid="quote-address" />
        )}
        {vis.show_license_number && (
          <Input placeholder="License number" value={form.license_number} onChange={update("license_number")} className={inputCls} data-testid="quote-license-number" />
        )}
        {vis.show_industry && (
          <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
            <SelectTrigger className={triggerCls} data-testid="quote-industry"><SelectValue placeholder="Industry" /></SelectTrigger>
            <SelectContent className={contentCls}>
              {INDUSTRIES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        {vis.show_inquiry_type && (
          <Select value={form.inquiry_type} onValueChange={(v) => set("inquiry_type", v)}>
            <SelectTrigger className={triggerCls} data-testid="quote-inquiry-type"><SelectValue placeholder="Type of inquiry" /></SelectTrigger>
            <SelectContent className={contentCls}>
              {INQUIRY_TYPES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        {vis.show_service && (
          <Select value={form.service} onValueChange={(v) => set("service", v)}>
            <SelectTrigger className={triggerCls} data-testid="quote-service"><SelectValue placeholder="Select service" /></SelectTrigger>
            <SelectContent className={contentCls}>
              {SERVICES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        {vis.show_damage_timing && (
          <Select value={form.damage_timing} onValueChange={(v) => set("damage_timing", v)}>
            <SelectTrigger className={triggerCls} data-testid="quote-damage-timing"><SelectValue placeholder="When did the water damage happen?" /></SelectTrigger>
            <SelectContent className={contentCls}>
              {DAMAGE_TIMING.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        {vis.show_power_available && (
          <Select value={form.power_available} onValueChange={(v) => set("power_available", v)}>
            <SelectTrigger className={triggerCls} data-testid="quote-power-available"><SelectValue placeholder="Is power currently available?" /></SelectTrigger>
            <SelectContent className={contentCls}>
              {POWER_AVAILABLE.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>

      {packageName && (
        <div className="mt-3 text-xs text-slate-400">
          Quoting: <span className="text-white font-medium">{packageName}</span>
        </div>
      )}

      {vis.show_message && (
        <Textarea
          placeholder="Please tell us what happened, when it started, what areas are affected, and whether the water source has been corrected."
          value={form.message} onChange={update("message")}
          className={`mt-4 ${inputCls}`} rows={4}
          data-testid="quote-message"
        />
      )}

      <button
        type="submit" disabled={loading}
        className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-60 hover:opacity-95 transition"
        data-testid="quote-submit"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Request Thermification™ Information
      </button>
    </form>
  );
};

export default QuoteForm;
